import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExcel,
  faCalendarAlt,
  faClipboardList,
  faFileAlt,
  faShippingFast,
  faShip,
  faTruck,
  faPlane,
  faQuestion,
  faMoneyBill,
  faMapMarkerAlt,
  faWarehouse,
  faUser,
  faBuilding,
  faIdCard,
  faFolder,
  faSpinner,
  faFileInvoiceDollar,
} from "@fortawesome/free-solid-svg-icons";
import logo from "../logo-wis.avif";

// Import the DocumentBrowser component
import DocumentBrowser from "./DocumentBrowser";
// Import the InvoiceTab component
import InvoiceTab from "./InvoiceTab";
// Import the new TrafficTimeline component
import TrafficTimeline from "./TrafficTimeline";
import ShipmentRiskAnalyzer from "./ShipmentRiskAnalyzer";
import WarehouseTab from "./WarehouseTab";
import TransportTab from "./TransportTab";
import YardTab from "./YardTab";

const TrafficDetailsControlTower = ({
  item,
  getTransportIcon,
  getOperationTypeText,
}) => {
  const [activeTab, setActiveTab] = useState("details");
  const [documentsMounted, setDocumentsMounted] = useState(false);
  const [invoiceMounted, setInvoiceMounted] = useState(false);
  const [timelineMounted, setTimelineMounted] = useState(false);
  const [riskAnalyzerMounted, setRiskAnalyzerMounted] = useState(false);
  const [warehouseMounted, setWarehouseMounted] = useState(false);
  const [transportMounted, setTransportMounted] = useState(false);
  const [yardMounted, setYardMounted] = useState(false);

  // When tab changes to documents, invoice, or timeline, set the appropriate state to true
  useEffect(() => {
    if (activeTab === "documents" && !documentsMounted) {
      setDocumentsMounted(true);
    }
    if (activeTab === "invoice" && !invoiceMounted) {
      setInvoiceMounted(true);
    }
    if (activeTab === "timeline" && !timelineMounted) {
      setTimelineMounted(true);
    }
    if (activeTab === "risk" && !riskAnalyzerMounted) {
      setRiskAnalyzerMounted(true);
    }
    if (activeTab === "warehouse" && !warehouseMounted) {
      setWarehouseMounted(true);
    }
    if (activeTab === "transport" && !transportMounted) {
      setTransportMounted(true);
    }
    if (activeTab === "yard" && !yardMounted) {
      setYardMounted(true);
    }
  }, [activeTab]);

  // Format date for display
  const formatDate = (date) => {
    if (!date) return "N/A";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(date).toLocaleDateString(undefined, options);
  };

  // Format time for display
  const formatTime = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get formatted date with time if available
  const getFormattedDateTime = (date) => {
    if (!date) return "N/A";
    return `${formatDate(date)} ${formatTime(date)}`;
  };

  // Get status color class
  const getStatusColorClass = (status) => {
    if (!status) return "bg-gray-100 text-gray-800";

    if (status === "Pagado") return "bg-green-100 text-green-800";
    return "bg-yellow-100 text-yellow-800";
  };

  return (
    <div className="space-y-4">
      {/* Tab Navigation */}
      <div className="bg-white">
        <div className="flex border-b border-gray-200">
          <button
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === "details"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("details")}
          >
            Traffic Details
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === "invoice"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("invoice")}
          >
            Invoice
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === "documents"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("documents")}
          >
            Documents
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === "timeline"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("timeline")}
          >
            Timeline
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
              activeTab === "warehouse"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("warehouse")}
          >
            Warehouse
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === "yard"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("yard")}
          >
            Yard
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
              activeTab === "transport"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("transport")}
          >
            Transport
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === "risk"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("risk")}
          >
            Risk
          </button>
        </div>
      </div>

      {activeTab === "details" && (
        <div className="space-y-4">
          {/* General Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2">
              General Information
            </h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-500">Reference:</span>
                <p className="font-medium">{item.header.reference}</p>
              </div>
              <div>
                <span className="text-gray-500">Operation Type:</span>
                <p className="font-medium">
                  {getOperationTypeText(item.header.operation_type)}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Status:</span>
                <p
                  className={`font-medium ${getStatusColorClass(
                    item.header.detail?.traffic_data?.status
                  )}`}
                >
                  {item.header.detail?.traffic_data?.status === "Pagado"
                    ? "Paid"
                    : "Open"}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Purchase Order:</span>
                <p className="font-medium">
                  {item.header.purchase_order || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2 flex items-center">
              <FontAwesomeIcon icon={faUser} className="mr-2 text-blue-500" />
              Customer Information
            </h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-500">Customer Name:</span>
                <p className="font-medium">{item.header.customer_name}</p>
              </div>
              <div>
                <span className="text-gray-500">Customer ID:</span>
                <p className="font-medium">{item.header.customer_id}</p>
              </div>
              <div>
                <span className="text-gray-500">Customer RFC:</span>
                <p className="font-medium">{item.header.customer_rfc}</p>
              </div>
              <div>
                <span className="text-gray-500">Division:</span>
                <p className="font-medium">
                  {item.header.customer_division || "N/A"}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Delivery Type:</span>
                <p className="font-medium">
                  {item.header.delivery_type || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Pedimento Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2 flex items-center">
              <FontAwesomeIcon
                icon={faFileAlt}
                className="mr-2 text-blue-500"
              />
              Pedimento Information
            </h3>
            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
              <div>
                <span className="text-gray-500">Pedimento Number:</span>
                <p className="font-medium">
                  {item.header.pedimento.pedimento_number || "N/A"}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Entry Point:</span>
                <p className="font-medium flex items-center">
                  <FontAwesomeIcon
                    icon={
                      getTransportIcon(
                        item.header.pedimento.pedimento_entry_point
                      ).icon
                    }
                    className={`${
                      getTransportIcon(
                        item.header.pedimento.pedimento_entry_point
                      ).color
                    } mr-1`}
                  />
                  {item.header.pedimento.pedimento_entry_point}
                </p>
              </div>
              <div>
                <span className="text-gray-500">License:</span>
                <p className="font-medium">
                  {item.header.pedimento.pedimento_license || "N/A"}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Consolidation:</span>
                <p className="font-medium">
                  {item.header.pedimento.pedimento_consolidation || "N/A"}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Remesa:</span>
                <p className="font-medium">
                  {item.header.pedimento.pedimento_remesa || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Transport Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2 flex items-center">
              <FontAwesomeIcon
                icon={faShippingFast}
                className="mr-2 text-blue-500"
              />
              Transport Information
            </h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-500">Trailer Number:</span>
                <p className="font-medium">
                  {item.header.trailer_number || "N/A"}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Total Packages:</span>
                <p className="font-medium">
                  {item.header.total_packages || "N/A"}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Weight:</span>
                <p className="font-medium">{item.header.weight || "N/A"}</p>
              </div>
              {item.header.detail?.traffic_data?.mexican_carrier && (
                <div>
                  <span className="text-gray-500">Mexican Carrier:</span>
                  <p className="font-medium">
                    {item.header.detail.traffic_data.mexican_carrier}
                  </p>
                </div>
              )}
              {item.header.detail?.traffic_data
                ?.mexican_carrier_trailer_number && (
                <div>
                  <span className="text-gray-500">MX Trailer #:</span>
                  <p className="font-medium">
                    {
                      item.header.detail.traffic_data
                        .mexican_carrier_trailer_number
                    }
                  </p>
                </div>
              )}
              {item.header.detail?.traffic_data?.us_carrier && (
                <div>
                  <span className="text-gray-500">US Carrier:</span>
                  <p className="font-medium">
                    {item.header.detail.traffic_data.us_carrier}
                  </p>
                </div>
              )}
              {item.header.detail?.traffic_data?.us_carrier_trailer_number && (
                <div>
                  <span className="text-gray-500">US Trailer #:</span>
                  <p className="font-medium">
                    {item.header.detail.traffic_data.us_carrier_trailer_number}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Date Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2 flex items-center">
              <FontAwesomeIcon
                icon={faCalendarAlt}
                className="mr-2 text-blue-500"
              />
              Date Information
            </h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {item.header.detail?.dates_data?.date_init && (
                <div>
                  <span className="text-gray-500">Created Date:</span>
                  <p className="font-medium">
                    {getFormattedDateTime(
                      item.header.detail.dates_data.date_init
                    )}
                  </p>
                </div>
              )}
              {item.header.detail?.dates_data?.date_inbound && (
                <div>
                  <span className="text-gray-500">Inbound Date:</span>
                  <p className="font-medium">
                    {getFormattedDateTime(
                      item.header.detail.dates_data.date_inbound
                    )}
                  </p>
                </div>
              )}
              {item.header.detail?.dates_data?.date_end && (
                <div>
                  <span className="text-gray-500">End Date:</span>
                  <p className="font-medium">
                    {getFormattedDateTime(
                      item.header.detail.dates_data.date_end
                    )}
                  </p>
                </div>
              )}
              {item.header.detail?.dates_data?.date_crossing && (
                <div>
                  <span className="text-gray-500">Crossing Date:</span>
                  <p className="font-medium">
                    {getFormattedDateTime(
                      item.header.detail.dates_data.date_crossing
                    )}
                  </p>
                </div>
              )}
              {item.header.detail?.dates_data?.date_pedimento_paid && (
                <div>
                  <span className="text-gray-500">Pedimento Paid Date:</span>
                  <p className="font-medium">
                    {getFormattedDateTime(
                      item.header.detail.dates_data.date_pedimento_paid
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Traffic Data */}
          {item.header.detail?.traffic_data && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2 flex items-center">
                <FontAwesomeIcon
                  icon={faFileExcel}
                  className="mr-2 text-green-600"
                />
                Traffic Data
              </h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {item.header.detail.traffic_data.clave_ped && (
                  <div>
                    <span className="text-gray-500">Clave Pedimento:</span>
                    <p className="font-medium">
                      {item.header.detail.traffic_data.clave_ped}
                    </p>
                  </div>
                )}
                {item.header.detail.traffic_data.regime && (
                  <div>
                    <span className="text-gray-500">Regime:</span>
                    <p className="font-medium">
                      {item.header.detail.traffic_data.regime}
                    </p>
                  </div>
                )}
                {item.header.detail.traffic_data.total_taxes && (
                  <div>
                    <span className="text-gray-500">Total Taxes:</span>
                    <p className="font-medium">
                      {item.header.detail.traffic_data.total_taxes}
                    </p>
                  </div>
                )}
                {item.header.detail.traffic_data.cargo && (
                  <div>
                    <span className="text-gray-500">Cargo:</span>
                    <p className="font-medium">
                      {item.header.detail.traffic_data.cargo}
                    </p>
                  </div>
                )}
              </div>

              {/* Observations */}
              {(item.header.detail.traffic_data.observations ||
                item.header.detail.traffic_data.traffic_observations ||
                item.header.detail.traffic_data.annotations) && (
                <div className="mt-4 space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">
                    Observations
                  </h4>
                  {item.header.detail.traffic_data.observations && (
                    <div className="bg-gray-50 p-2 rounded">
                      <span className="block text-xs text-gray-500 mb-1">
                        General Observations:
                      </span>
                      <p className="text-sm">
                        {item.header.detail.traffic_data.observations}
                      </p>
                    </div>
                  )}
                  {item.header.detail.traffic_data.traffic_observations && (
                    <div className="bg-gray-50 p-2 rounded">
                      <span className="block text-xs text-gray-500 mb-1">
                        Traffic Observations:
                      </span>
                      <p className="text-sm">
                        {item.header.detail.traffic_data.traffic_observations}
                      </p>
                    </div>
                  )}
                  {item.header.detail.traffic_data.annotations && (
                    <div className="bg-gray-50 p-2 rounded">
                      <span className="block text-xs text-gray-500 mb-1">
                        Annotations:
                      </span>
                      <p className="text-sm">
                        {item.header.detail.traffic_data.annotations}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Bill of Lading Information */}
          {item.header.bill_of_lading_data &&
            item.header.bill_of_lading_data.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2 flex items-center">
                  <FontAwesomeIcon
                    icon={faClipboardList}
                    className="mr-2 text-blue-500"
                  />
                  Bill of Lading Information
                </h3>
                {item.header.bill_of_lading_data.map((bol, index) => (
                  <div key={index} className="mb-3 last:mb-0">
                    {index > 0 && (
                      <div className="border-t border-gray-100 my-3"></div>
                    )}
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Bill of Lading:</span>
                        <p className="font-medium">
                          {bol.bill_of_lading || "N/A"}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Urgent:</span>
                        <p className="font-medium">
                          {bol.bill_of_lading_urgent === "1" ? "Yes" : "No"}
                        </p>
                      </div>
                      {bol.date_created_bill_of_lading_urgent && (
                        <div>
                          <span className="text-gray-500">
                            Created Date (Urgent):
                          </span>
                          <p className="font-medium">
                            {formatDate(bol.date_created_bill_of_lading_urgent)}
                          </p>
                        </div>
                      )}
                      {bol.date_arrival_bill_of_lading_urgent && (
                        <div>
                          <span className="text-gray-500">
                            Arrival Date (Urgent):
                          </span>
                          <p className="font-medium">
                            {formatDate(bol.date_arrival_bill_of_lading_urgent)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}

      {/* Invoice Tab */}
      {activeTab === "invoice" && (
        <div>{invoiceMounted && <InvoiceTab item={item} />}</div>
      )}

      {/* Documents Tab */}
      {activeTab === "documents" && (
        <div>{documentsMounted && <DocumentBrowser item={item} />}</div>
      )}

      {/* Timeline Tab - Updated to use the new TrafficTimeline component */}
      {activeTab === "timeline" && (
        <div>{timelineMounted && <TrafficTimeline item={item} />}</div>
      )}

      {activeTab === "risk" && (
        <div>{riskAnalyzerMounted && <ShipmentRiskAnalyzer item={item} />}</div>
      )}

      {activeTab === "warehouse" && (
        <div>{warehouseMounted && <WarehouseTab item={item} />}</div>
      )}

      {activeTab === "transport" && (
        <div>{transportMounted && <TransportTab item={item} />}</div>
      )}
      {activeTab === "yard" && (
        <div>{yardMounted && <YardTab item={item} />}</div>
      )}
    </div>
  );
};

export default TrafficDetailsControlTower;
